import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar, Rating } from '@mui/material';
import { FormatQuote } from '@mui/icons-material';

const testimonials = [
  {
    name: "Rajesh Kumar",
    role: "Professional Cricket Player",
    avatar: "/images/testimonials/rajesh.jpg",
    rating: 5,
    text: "Dr. Gopi's expertise in sports physiotherapy is exceptional. His treatment helped me recover from my shoulder injury and return to cricket stronger than ever. His dedication to athletes' well-being is remarkable.",
    gradient: "linear-gradient(45deg, #6b46c1, #805ad5)"
  },
  {
    name: "Priya Sharma",
    role: "Software Engineer",
    avatar: "/images/testimonials/priya.jpg",
    rating: 5,
    text: "After suffering from chronic neck pain due to long working hours, Dr. Gopi's orthopedic physiotherapy treatment made a significant difference. His personalized approach and expertise are truly commendable.",
    gradient: "linear-gradient(45deg, #2B6CB0, #4299E1)"
  },
  {
    name: "Amit Patel",
    role: "Yoga and Meditation Instructor",
    avatar: "/images/testimonials/amit.jpg",
    rating: 5,
    text: "Dr. Gopi's manual therapy techniques are outstanding. His deep understanding of body mechanics and holistic approach to treatment have helped me maintain peak performance in my yoga practice.",
    gradient: "linear-gradient(45deg, #38A169, #48BB78)"
  },
  {
    name: "Deepak Verma",
    role: "Heart Surgery Recovery Patient",
    avatar: "/images/testimonials/deepak.jpg",
    rating: 5,
    text: "Dr. Gopi's cardiac rehabilitation program gave me a new lease on life. His careful monitoring and encouragement throughout my recovery journey were invaluable. I'm grateful for his expertise.",
    gradient: "linear-gradient(45deg, #E53E3E, #FC8181)"
  },
  {
    name: "Anjali Desai",
    role: "New Mother",
    avatar: "/images/testimonials/anjali.jpg",
    rating: 5,
    text: "Dr. Gopi's expertise in women's health physiotherapy was crucial during my postpartum recovery. His understanding, professionalism, and effective treatment plan helped me regain strength quickly.",
    gradient: "linear-gradient(45deg, #D53F8C, #F687B3)"
  },
  {
    name: "Ramesh Iyer",
    role: "Retired Professor",
    avatar: "/images/testimonials/ramesh.jpg",
    rating: 5,
    text: "At 70, I was worried about my mobility issues, but Dr. Gopi's geriatric physiotherapy program has been transformative. His patient-centric approach and genuine care make him an exceptional therapist.",
    gradient: "linear-gradient(45deg, #744210, #975A16)"
  },
  {
    name: "Meena Krishnan",
    role: "Vertigo Patient",
    avatar: "/images/testimonials/meena.jpg",
    rating: 5,
    text: "Dr. Gopi's expertise in vestibular rehabilitation helped me overcome severe vertigo. His methodical treatment approach and constant support made my recovery journey much easier.",
    gradient: "linear-gradient(45deg, #805AD5, #B794F4)"
  },
  {
    name: "Suresh Menon",
    role: "Stroke Recovery Patient",
    avatar: "/images/testimonials/suresh.jpg",
    rating: 5,
    text: "Post-stroke recovery seemed daunting until I met Dr. Gopi. His expertise in neurological rehabilitation and unwavering support helped me regain my independence. He's truly a blessing.",
    gradient: "linear-gradient(45deg, #285E61, #319795)"
  },
  {
    name: "Lakshmi Rao",
    role: "Home Care Patient",
    avatar: "/images/testimonials/lakshmi.jpg",
    rating: 5,
    text: "Dr. Gopi's home care physiotherapy service has been exceptional. His professional expertise combined with his compassionate nature makes him stand out. I'm grateful for his dedicated care.",
    gradient: "linear-gradient(45deg, #319795, #4FD1C5)"
  }
];

const TestimonialsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      });
    }, observerOptions);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Show only 6 testimonials initially
  const displayedTestimonials = testimonials.slice(0, 6);

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          background: 'radial-gradient(circle at 25% 25%, rgba(0, 150, 136, 0.1) 0%, transparent 40%), radial-gradient(circle at 75% 75%, rgba(0, 150, 136, 0.1) 0%, transparent 40%)',
          zIndex: 0,
          animation: 'pulse 15s infinite',
          '@keyframes pulse': {
            '0%': { opacity: 0.4 },
            '50%': { opacity: 0.2 },
            '100%': { opacity: 0.4 }
          }
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: { xs: 6, md: 8 },
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <Typography
            variant="subtitle1"
            sx={{ 
              color: 'primary.main', 
              mb: 2,
              fontWeight: 600,
              letterSpacing: 1.2
            }}
          >
            TESTIMONIALS
          </Typography>
          <Typography 
            variant="h3" 
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            What Our Patients Say
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.8
            }}
          >
            Read about the experiences of our patients and how our physiotherapy services have helped them recover and improve their quality of life.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {displayedTestimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
                  transitionDelay: `${index * 0.2}s`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <FormatQuote 
                      sx={{ 
                        fontSize: '3rem',
                        color: 'primary.main',
                        opacity: 0.2,
                        transform: 'rotate(180deg)'
                      }} 
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      lineHeight: 1.7,
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      minHeight: '120px'
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      sx={{
                        width: 56,
                        height: 56,
                        mr: 2,
                        border: '2px solid',
                        borderColor: 'primary.main'
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          background: testimonial.gradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                  <Rating 
                    value={testimonial.rating} 
                    readOnly 
                    sx={{
                      color: 'primary.main'
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection; 